import React, { useState, useEffect } from 'react';
import { Target, DollarSign, Brain, BookOpen } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getGoals } from '../../../services/dashboardAnalytics';
import { getDashboardAnalytics } from '../../../services/dashboardAnalytics';

const GOAL_CONFIG = {
  monthly_profit: {
    icon: DollarSign,
    label: 'Monthly Profit',
    format: (val) => `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    formatTarget: (val) => `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
    inverted: false,
  },
  reduce_emotional: {
    icon: Brain,
    label: 'Reduce Emotional Trades',
    format: (val) => String(Math.round(val)),
    formatTarget: (val) => `${val} max`,
    inverted: true,
  },
  journal_consistency: {
    icon: BookOpen,
    label: 'Journal Consistency',
    format: (val) => String(Math.round(val)),
    formatTarget: (val) => `${val} days`,
    inverted: false,
  },
};

const getProgressColor = (percentage) => {
  if (percentage >= 80) return 'bg-green-500';
  if (percentage >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
};

const GoalsSummaryCard = () => {
  const navigate = useNavigate();
  const [goals, setGoals] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [goalsRes, analyticsRes] = await Promise.all([
          getGoals(),
          getDashboardAnalytics(),
        ]);
        setGoals(goalsRes.goals || []);
        setProgress(analyticsRes.goal_progress || []);
      } catch (err) {
        console.error('Failed to load goals:', err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const mergedGoals = goals.map((goal) => {
    const prog = progress.find((p) => p.goal_type === goal.goal_type);
    return {
      ...goal,
      current_value: prog?.current_value ?? 0,
      percentage: prog?.percentage ?? 0,
    };
  });

  if (loading) {
    return (
      <div className="bg-card text-card-foreground rounded-lg shadow-sm border border-border p-4">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-muted rounded w-1/3" />
          <div className="h-2 bg-muted rounded w-full" />
          <div className="h-2 bg-muted rounded w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card text-card-foreground rounded-lg shadow-sm border border-border p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Target className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">Trading Goals</h3>
        </div>
        <button
          onClick={() => navigate('/profile')}
          className="text-xs text-primary hover:text-primary/80 transition-colors"
        >
          Edit Goals
        </button>
      </div>

      {mergedGoals.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          No goals set.{' '}
          <button onClick={() => navigate('/profile')} className="text-primary hover:text-primary/80">
            Set goals in Profile
          </button>
        </p>
      ) : (
        <div className="space-y-3">
          {mergedGoals.map((goal) => {
            const config = GOAL_CONFIG[goal.goal_type];
            if (!config) return null;
            const Icon = config.icon;
            const barWidth = Math.min(100, Math.max(0, goal.percentage));

            return (
              <div key={goal.goal_type}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 text-muted-foreground" />
                    <span className="text-xs font-medium text-foreground">{config.label}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {config.format(goal.current_value)} / {config.formatTarget(goal.target_value)}
                  </span>
                </div>
                <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${getProgressColor(goal.percentage)}`}
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalsSummaryCard;
